import React, { useLayoutEffect, useRef, useState } from "react";
import { Button, Icon } from "./StudioControls";
import { ROLES } from "./studioModel";
import { groupPath, layerTree, layerMembers, renameGroup, selectionEntries } from "./studioGroups";

export default function StudioLayers({ page, selected, setSelected, focusedLayerId, patch, moveLayer, commit, group, ungroup }) {
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(new Set());
  const [renaming, setRenaming] = useState(null);
  const [name, setName] = useState("");
  const [drop, setDrop] = useState(null);
  const [dragging, setDragging] = useState([]);
  const rowRefs = useRef(new Map());
  const reveal = useRef(null);
  const tree = layerTree(page);
  const entries = selectionEntries(page, selected, focusedLayerId);
  const selectionKey = `${page.id}:${focusedLayerId || ""}:${selected.join(",")}`;
  useLayoutEffect(() => {
    const targets = selectionEntries(page, selected, focusedLayerId);
    const ancestors = new Set();
    targets.forEach((entry) => {
      const path = groupPath(layerMembers(entry)[0]);
      const parents = entry.group ? path.slice(0, path.findIndex((g) => g.id === entry.id)) : path;
      parents.forEach((g) => ancestors.add(g.id));
    });
    setCollapsed((old) => new Set([...old].filter((id) => !ancestors.has(id))));
    reveal.current = targets[0]?.id;
    // Selection changes should reveal an item even when a previous search hid it.
    setQuery("");
    // The key tracks selection, not geometry edits or renaming.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);
  useLayoutEffect(() => {
    const row = rowRefs.current.get(reveal.current);
    if (!row) return;
    const panel = row.closest(".ms-library");
    if (!panel) return;
    const rect = row.getBoundingClientRect(), view = panel.getBoundingClientRect();
    if (rect.top < view.top || rect.bottom > view.bottom) {
      panel.scrollTop += rect.top - view.top - Math.max(0, (view.height - rect.height) / 2);
    }
    reveal.current = null;
  });
  const toggle = (id) => setCollapsed((old) => { const next = new Set(old); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const finishRename = () => {
    if (renaming && name.trim()) commit((d) => ({ ...d, pages: d.pages.map((p) => p.id === page.id ? renameGroup(p, renaming, name) : p) }));
    setRenaming(null);
  };
  const select = (event, ids, focusId) => setSelected(event.shiftKey
    ? ids.every((id) => selected.includes(id)) ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]
    : ids, event.shiftKey ? null : focusId);
  const applyDrop = (event, target, placement) => {
    event.preventDefault(); event.stopPropagation();
    let ids = dragging;
    if (!ids.length) { try { ids = JSON.parse(event.dataTransfer.getData("application/x-menu-studio-layers")); } catch { return; } }
    moveLayer(ids, target, placement);
    if (placement === "inside" && target) setCollapsed((old) => { const next = new Set(old); next.delete(target); return next; });
    setDrop(null); setDragging([]);
  };
  const matches = (entry) => `${entry.name || ""} ${ROLES[entry.role] || ""}`.toLowerCase().includes(query.toLowerCase()) || (entry.group && entry.children.some(matches));
  const render = (entry, depth = 0, ancestorMatches = false) => {
    if (query && !ancestorMatches && !matches(entry)) return null;
    const members = layerMembers(entry), ids = members.map((n) => n.id);
    const active = entries.some((e) => e.id === entry.id);
    const partial = !active && ids.some((id) => selected.includes(id));
    const locked = members.every((n) => n.locked), hidden = members.every((n) => n.hidden);
    const open = !collapsed.has(entry.id) || !!query;
    const target = drop?.id === entry.id ? drop.placement : "";
    return <React.Fragment key={entry.id}>
      <div ref={(element) => { if (element) rowRefs.current.set(entry.id, element); else rowRefs.current.delete(entry.id); }} role="treeitem" aria-expanded={entry.group ? open : undefined} aria-selected={active} aria-level={depth + 1}
        className={`ms-layer ms-tree-layer ${entry.group ? "is-group" : ""} ${active ? "is-active" : ""} ${partial ? "is-partial" : ""} ${hidden ? "is-hidden" : ""} ${target ? `drop-${target}` : ""}`}
        style={{ "--layer-depth": depth }} draggable={!members.some((n) => n.locked) && renaming !== entry.id}
        onDragStart={(event) => {
          event.stopPropagation();
          const moving = active ? entries.map((e) => e.id) : [entry.id];
          setDragging(moving); event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("application/x-menu-studio-layers", JSON.stringify(moving));
          if (!active) setSelected(ids, entry.id);
        }}
        onDragEnd={() => { setDrop(null); setDragging([]); }}
        onDragOver={(event) => {
          if (!dragging.length) return;
          event.preventDefault(); event.stopPropagation();
          const rect = event.currentTarget.getBoundingClientRect(), ratio = (event.clientY - rect.top) / rect.height;
          const placement = entry.group && ratio > .25 && ratio < .75 ? "inside" : ratio < .5 ? "before" : "after";
          setDrop({ id: entry.id, placement }); event.dataTransfer.dropEffect = "move";
        }}
        onDrop={(event) => applyDrop(event, entry.id, target || (entry.group ? "inside" : "before"))}>
        {entry.group ? <button className="ms-layer-disclosure" aria-label={`${open ? "Collapse" : "Expand"} ${entry.name}`} onClick={() => toggle(entry.id)}><Icon name={open ? "down" : "chevron"} size={12} /></button> : <span className="ms-layer-disclosure" />}
        {renaming === entry.id ? <input className="ms-layer-rename" aria-label="Group name" autoFocus value={name} maxLength={100}
          onFocus={(e) => e.target.select()} onChange={(e) => setName(e.target.value)} onBlur={finishRename}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); finishRename(); } if (e.key === "Escape") setRenaming(null); }} />
          : <button className="ms-layer-select" onClick={(e) => select(e, ids, entry.id)}
            onDoubleClick={entry.group ? () => { setRenaming(entry.id); setName(entry.name); } : undefined}>
            <Icon name={entry.group ? "layers" : entry.type === "text" ? "text" : entry.type === "image" ? "image" : "shapes"} size={14} />
            <span><b>{entry.name || "Untitled layer"}</b><small>{entry.group ? `${entry.children.length} ${entry.children.length === 1 ? "layer" : "layers"}` : ROLES[entry.role]}</small></span>
          </button>}
        <Button icon={locked ? "lock" : "unlock"} title={`${locked ? "Unlock" : "Lock"} ${entry.name}`} onClick={() => patch({ locked: !locked }, true, ids)} />
        <Button icon="eye" title={`${hidden ? "Show" : "Hide"} ${entry.name}`} active={!hidden} onClick={() => patch({ hidden: !hidden }, true, ids)} />
      </div>
      {entry.group && open && entry.children.map((child) => render(child, depth + 1, ancestorMatches || (!!query && entry.name.toLowerCase().includes(query.toLowerCase()))))}
    </React.Fragment>;
  };
  return <>
    <div className="ms-search"><Icon name="search" size={15} /><input aria-label="Search layers" placeholder="Find a layer…" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
    <div className="ms-layer-actions"><Button onClick={group} disabled={entries.length < 2}>Group</Button><Button onClick={ungroup} disabled={!page.elements.some((n) => selected.includes(n.id) && groupPath(n).length)}>Ungroup</Button>
      <Button title="Move selected layers out to the page" onClick={() => moveLayer(entries.map((e) => e.id), null, "inside")} disabled={!entries.some((e) => e.parentId)}>To page</Button></div>
    <p className="ms-hint ms-inset">Drag into groups or between layers. Double-click a group to rename.</p>
    <div className="ms-layer-list" role="tree" aria-label="Page layers" aria-multiselectable="true">{tree.map((entry) => render(entry))}</div>
    <div className={`ms-layer-root-drop ${drop?.id === null ? "is-over" : ""}`} onDragOver={(e) => { if (dragging.length) { e.preventDefault(); setDrop({ id: null, placement: "inside" }); } }} onDrop={(e) => applyDrop(e, null, "inside")}>
      {dragging.length ? "Drop here to move out to the page" : "Page level"}
    </div>
    {!page.elements.length && <p className="ms-hint ms-inset">This page is a blank canvas. Add text or artwork to begin.</p>}
  </>;
}
